import React, { useEffect, useMemo, useState } from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import { setNumberValueAs } from "../../../../../../../utils/react-hook-form";
import { FlexRow } from "../../../../../../Common/__styles__/Layout";
import { Button } from "../../../../../../Common/Button";
import { Label, Select } from "../../../../../../Inputs";
import { Text } from "../../../../../../Inputs/react-hook-form";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import {
  InputFieldPath,
  SubmissionsBuilderFieldSettings,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { FieldWrapper } from "../__styles__/SidebarColumn";
import { HelpText, WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import LabelValueOptionsInput from "./LabelValueOptionsInput";
import {
  DefaultValuesSection,
  DisplaySettingsSection,
  FieldPermissionsConfig,
  FieldSettingConfig,
  NumberDynamicDefaultInput,
  NumberStaticDefaultInput,
} from "./shared";

const NumberWithUnitsFieldSettings = ({
  fieldPath,
  isModuleInput,
  readOnly,
}: SelectedSubmissionsBuilderField & SubmissionsBuilderFieldSettings) => {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext<SubmissionsBuilderFormDataStructure>();

  const options = watch(`${fieldPath}.enum`);
  const optionsAsObjects = options?.map(
    (option: string | { label: string; value: string }) => {
      if (typeof option === "string") {
        return {
          label: option,
          value: option,
        };
      }
      return option;
    },
  );
  const [dropdownOptions, setDropdownOptions] = useState<
    { label: string; value: string }[]
  >(optionsAsObjects?.length ? optionsAsObjects : [{ label: "", value: "" }]);

  const setDropdownOption = (index: number, label: string, value: string) => {
    const updatedOptions = [...dropdownOptions];
    updatedOptions[index] = { label, value };
    setDropdownOptions(updatedOptions);
    setValue(`${fieldPath}.enum`, updatedOptions, { shouldValidate: true });
  };

  const addDropdownOption = () => {
    const updatedOptions = [...dropdownOptions, { label: "", value: "" }];
    setDropdownOptions(updatedOptions);
    setValue(`${fieldPath}.enum`, updatedOptions, { shouldValidate: true });
  };

  const removeDropdownOption = (index: number) => {
    dropdownOptions.splice(index, 1);
    // creating a shallow copy of the array since splice modifies in place
    const updatedOptions = [...dropdownOptions];
    setDropdownOptions(updatedOptions);
    setValue(`${fieldPath}.enum`, updatedOptions, { shouldValidate: true });
  };

  return (
    <WidgetSettingsContainer>
      <FieldSettingConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={false}
        readOnly={readOnly}
      />
      <div>
        <Label text={"Helper text"} htmlFor={`${fieldPath}.helperText`} />
        <Text {...register(`${fieldPath}.helperText`)} disabled={readOnly} />
      </div>
      <FlexRow style={{ gap: "16px" }}>
        <Text
          {...register(`${fieldPath}.minimum`, {
            setValueAs: setNumberValueAs,
            deps: [`${fieldPath}.maximum`],
          })}
          label="Number minimum"
          type="number"
          error={get(errors, `${fieldPath}.minimum.message`, "")}
          disabled={readOnly}
        />
        <Text
          {...register(`${fieldPath}.maximum`, {
            setValueAs: setNumberValueAs,
            deps: [`${fieldPath}.minimum`],
          })}
          label="Number maximum"
          type="number"
          error={get(errors, `${fieldPath}.maximum.message`, "")}
          disabled={readOnly}
        />
      </FlexRow>
      <div>
        <div>
          <Label
            style={{ marginBottom: "4px" }}
            required
            text={"Dropdown options"}
          />
          <HelpText style={{ marginTop: "0px", paddingBottom: "8px" }}>
            Labels are visible to users, while values are backend identifiers
            and hidden from them.
          </HelpText>
        </div>
        <LabelValueOptionsInput
          dropdownOptions={dropdownOptions}
          setDropdownOption={setDropdownOption}
          removeDropdownOption={removeDropdownOption}
          valueEditingDisabled={isModuleInput}
          disabled={readOnly}
        />
        <div style={{ paddingTop: "8px" }}>
          <Button
            styleVariant="outlineLight"
            size="small"
            leftIconName="plus"
            onClick={addDropdownOption}
            disabled={isModuleInput || readOnly}
          >
            Add option
          </Button>
        </div>
      </div>
      <DefaultValuesSection showTooltip>
        <FieldWrapper data-testid="dynamic-default-value-wrapper">
          <NumberDynamicDefaultInput
            fieldPath={fieldPath}
            readOnly={readOnly}
          />
        </FieldWrapper>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <NumberStaticDefaultInput fieldPath={fieldPath} readOnly={readOnly} />
        </FieldWrapper>
        <FieldWrapper data-testid="units-static-default-value-wrapper">
          <UnitsStaticDefaultInput
            fieldPath={fieldPath}
            readOnly={readOnly}
            dropdownOptions={dropdownOptions}
          />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} />
      <DisplaySettingsSection
        fieldPath={fieldPath}
        isNestedField
        readOnly={readOnly}
      />
    </WidgetSettingsContainer>
  );
};

export const UnitsStaticDefaultInput = ({
  fieldPath,
  readOnly,
  dropdownOptions,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
  dropdownOptions: Array<{ label: string; value: string }>;
}) => {
  const { control, watch, setValue } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const dropdownOptionsValuesSet = useMemo(() => {
    return new Set(dropdownOptions.map(option => option.value));
  }, [dropdownOptions]);

  const value = watch(`${fieldPath}.unitsDefaultValue`);

  useEffect(() => {
    if (value && !dropdownOptionsValuesSet.has(value as string)) {
      // ensure default value is reset if the selected dropdown option is removed
      setValue(`${fieldPath}.unitsDefaultValue`, undefined);
    }
  }, [value, dropdownOptionsValuesSet, fieldPath, setValue]);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.unitsDefaultValue`}
      render={({ field }) => {
        return (
          <Select
            value={value ?? null}
            label="Units default value"
            name={`${fieldPath}.unitsDefaultValue`}
            onChange={value => {
              // store no value (undefined) for ui:unitsDefaultValue instead of empty string in form state
              field.onChange(value ?? undefined);
            }}
            options={dropdownOptions}
            isClearable={true}
            disabled={readOnly}
          />
        );
      }}
    />
  );
};

NumberWithUnitsFieldSettings.displayName = "NumberWithUnitsFieldSettings";

export default NumberWithUnitsFieldSettings;
